import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Message, Messages, MessageService } from '../../domain/messages.domain';
import { MessagesCounterService } from './messages-counter.service';
import { MessagesRestService } from './messages-rest.service';

/**
 * This Service is used when WebBroker starts up to retrieve messages from the backend and do two things:
 * 1. Show unread messages counter on the menu
 * 2. Determine if there are critical or unread essential messages that require the messages popup to be shown
 */
@Injectable({
  providedIn: 'root'
})
export class MessagesService implements MessageService {

  onMessagesRead: BehaviorSubject<boolean>;
  protected unreadMessages: number = 0;
  protected criticalOrEssentialMessages: Array<Message>;
  protected messagesRead: boolean = false;

  constructor(protected messagesRestService: MessagesRestService,
              protected messagesCounterService: MessagesCounterService) {
    this.onMessagesRead = new BehaviorSubject<boolean>(false);
  }

  public static getUnreadMessages(messages: Array<Message>): number {
    return messages.reduce((prev: number, next: Message): number => {
      if (next) {
        return next.read ? prev : prev + 1;
      }
    }, 0);
  }

  protected static criticalOrEssentialMessagesFilter(messages: Array<Message>): Array<Message> {
    if (messages instanceof Array && messages.length > 0) {
      return messages
        .filter((message: Message): boolean => message.importance === 'CRITICAL' || (message.importance === 'ESSENTIAL' && !message.read));
    }
  }

  /**
   * This function retrieves the messages from the backend on startup, and then counts the number of unread messages to show in the menu,
   * as well as filtering out the critical or unread essential messages to be shown in the popup modal if there are any
   *
   * @returns resolves to true when there are critical messages
   */
  retrieveStartupMessages(): Observable<boolean> {
    return this.messagesRestService.retrieveMessages({ isActive: true })
      .pipe(
        map((data: Messages): boolean => {
          if (data !== undefined && data.messages !== undefined) {
            this.setUnreadMessages(MessagesService.getUnreadMessages(data.messages));
            this.messagesCounterService.setGeneralMessagesCounter(this.unreadMessages);
            this.messagesCounterService.setMessagesCounter(this.unreadMessages);
            this.criticalOrEssentialMessages = MessagesService.criticalOrEssentialMessagesFilter(data.messages);
            if (this.criticalOrEssentialMessages instanceof Array && this.criticalOrEssentialMessages.length > 0) {
              return true;
            }
            this.setModalMessagesRead(true);
          }
          return false;
        })
      );

  }

  retrieveMessages(): Promise<Array<Message>> {
    return lastValueFrom(this.messagesRestService
      .retrieveMessages({ isActive: true })
      .pipe(
        map((data: Messages): Array<Message> => {
          if (data !== undefined && data.messages !== undefined) {
            return data.messages;
          }
        })
      ));
  }

  getCriticalOrEssentialMessages(): Array<Message> {
    return this.criticalOrEssentialMessages;
  }

  setModalMessagesRead(messagesRead: boolean): void {
    this.messagesRead = messagesRead;
    this.onMessagesRead.next(this.messagesRead);
  }

  markMessagesAsRead(messages: Array<Message>, updateMenuCounter: boolean): void {
    if (messages !== undefined && messages instanceof Array && messages.length > 0) {
      this.messagesRestService.markAsRead(messages).subscribe((success: boolean): void => {
        if (updateMenuCounter && success) {
          this.setUnreadMessages(0);
          this.messagesCounterService.setMessagesCounter(this.unreadMessages);
        }
      });
    }
  }

  protected setUnreadMessages(unreadMessages: number): void {
    this.unreadMessages = unreadMessages;
  }
}
